import { useContext, useRef } from 'react';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from '@ea-lab/reactive-json';
import { decimalToHMLabel } from './Timesheet';

/**
 * Read-only display of the task RAF, in hours/minutes (« 1h30 »).
 *
 * Le RAF suit le modèle HYBRIDE du serveur (cf. _orbit_task_declared_time_left) :
 *   • un « reste à faire » DÉCLARÉ sur un log fait foi → on affiche le RAF STOCKÉ
 *     (storedRaf) ; l'estimation ne doit alors PAS le modifier ;
 *   • sinon le RAF est DÉRIVÉ = max(0, estimation − temps passé) → on l'affiche en
 *     direct pour qu'il réagisse aux éditions d'estimation, sans aller-retour.
 *
 * Le mode (déclaré vs dérivé) est figé à partir des valeurs SERVEUR (temps passé
 * + RAF stockés, rafraîchis ENSEMBLE via l'event task:entity-updated). Il n'est
 * PAS recalculé à chaque frappe dans l'estimation — sinon éditer l'estimation
 * ferait basculer le mode à tort.
 *
 * Props:
 *   estimation  – chemin vers l'estimation (h, valeur éditable en direct)
 *   timeSpent   – chemin vers le temps passé cumulé (h, serveur)
 *   storedRaf   – chemin vers le field_raf stocké (h, serveur) — la vérité hybride
 */
export const RafComputedField = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);

  const est = Number(evaluateTemplateValue({ valueToEvaluate: props.estimation, globalDataContext, templateContext })) || 0;
  const spent = Number(evaluateTemplateValue({ valueToEvaluate: props.timeSpent, globalDataContext, templateContext })) || 0;
  const storedRaw = evaluateTemplateValue({ valueToEvaluate: props.storedRaf, globalDataContext, templateContext });
  const stored = (storedRaw === '' || storedRaw === null || storedRaw === undefined) ? null : Number(storedRaw);

  // Mode figé, ré-évalué seulement quand le couple SERVEUR (spent, stored) change
  // — jamais sur une simple frappe dans l'estimation.
  const modeRef = useRef({ key: null, declared: false });
  const key = `${spent}|${stored}`;
  if (modeRef.current.key !== key) {
    const derived = Math.max(0, est - spent);
    // Déclaré ⇔ le RAF stocké ne correspond PAS au dérivé (un reste a été posé).
    modeRef.current = { key, declared: stored !== null && Math.abs(stored - derived) >= 0.005 };
  }

  const raf = (modeRef.current.declared && stored !== null)
    ? stored
    : Math.max(0, est - spent);

  return <>{decimalToHMLabel(raf)}</>;
};

export default RafComputedField;
